import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@geely/ui';
import { appointmentsApi } from '@/lib/api/appointments.api';

type ConfirmationState =
  | { status: 'loading' }
  | { status: 'success'; appointment: AppointmentInfo; message: string }
  | { status: 'already-confirmed'; appointment: AppointmentInfo; message: string }
  | { status: 'expired' }
  | { status: 'error'; message: string };

interface AppointmentInfo {
  id: string;
  date: string;
  time: string;
  customerName: string;
}

function formatDateBR(date: string): string {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

export function ConfirmBookingPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ConfirmationState>({ status: 'loading' });

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setState({ status: 'error', message: 'Link de confirmação inválido.' });
      return;
    }

    appointmentsApi
      .confirmPresence(token)
      .then((response) => {
        const { alreadyConfirmed, appointment, message } = response.data;

        if (alreadyConfirmed) {
          setState({ status: 'already-confirmed', appointment, message });
        } else {
          setState({ status: 'success', appointment, message });
        }
      })
      .catch((error) => {
        const status = error?.response?.status;
        const code = error?.response?.data?.code;

        if (status === 410 || code === 'TOKEN_EXPIRED') {
          setState({ status: 'expired' });
        } else {
          setState({
            status: 'error',
            message: error?.response?.data?.error || 'Ocorreu um erro ao confirmar sua presença.',
          });
        }
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-wider">GEELY CARE</h1>
        </div>

        {state.status === 'loading' && <LoadingState />}
        {state.status === 'success' && <SuccessState appointment={state.appointment} />}
        {state.status === 'already-confirmed' && <AlreadyConfirmedState appointment={state.appointment} />}
        {state.status === 'expired' && <ExpiredState />}
        {state.status === 'error' && <ErrorState message={state.message} />}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4" />
        <p className="text-gray-500">Confirmando sua presença...</p>
      </CardContent>
    </Card>
  );
}

function SuccessState({ appointment }: { appointment: AppointmentInfo }) {
  return (
    <Card className="border-green-200">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <CardTitle className="text-green-700">Presença Confirmada!</CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <p className="text-gray-600">
          Olá, <strong>{appointment.customerName}</strong>! Sua presença foi confirmada com sucesso.
        </p>
        <div className="bg-green-50 rounded-lg p-4 text-left space-y-2">
          <p className="text-sm text-green-800">
            <span className="font-medium">Data:</span> {formatDateBR(appointment.date)}
          </p>
          <p className="text-sm text-green-800">
            <span className="font-medium">Horário:</span> {appointment.time}
          </p>
        </div>
        <p className="text-sm text-gray-500">Aguardamos você na data marcada!</p>
      </CardContent>
    </Card>
  );
}

function AlreadyConfirmedState({ appointment }: { appointment: AppointmentInfo }) {
  return (
    <Card className="border-blue-200">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <CardTitle className="text-blue-700">Já Confirmado</CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <p className="text-gray-600">
          Olá, <strong>{appointment.customerName}</strong>! Sua presença já foi confirmada anteriormente.
        </p>
        <div className="bg-blue-50 rounded-lg p-4 text-left space-y-2">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Data:</span> {formatDateBR(appointment.date)}
          </p>
          <p className="text-sm text-blue-800">
            <span className="font-medium">Horário:</span> {appointment.time}
          </p>
        </div>
        <p className="text-sm text-gray-500">Aguardamos você na data marcada!</p>
      </CardContent>
    </Card>
  );
}

function ExpiredState() {
  return (
    <Card className="border-amber-200">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
          <svg className="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <CardTitle className="text-amber-700">Link Expirado</CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <p className="text-gray-600">
          Este link de confirmação expirou. Entre em contato com a concessionária ou busque seu agendamento no sistema.
        </p>
        <Button asChild className="w-full">
          <Link to="/lookup">Buscar Meu Agendamento</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <Card className="border-red-200">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <CardTitle className="text-red-700">Erro</CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <p className="text-gray-600">{message}</p>
        <Button asChild className="w-full">
          <Link to="/lookup">Buscar Meu Agendamento</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
